import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, fmtMoney, downloadWithToast } from "@/lib/apiClient";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import SendEmailDialog from "@/components/sales/SendEmailDialog";
import QuoteEditorSheet from "@/pages/sales/QuoteEditorSheet";
import {
  Plus, Trash, FilePdf, FileXls, ArrowsClockwise, MagnifyingGlass, PencilLine,
  ArrowRight, CheckCircle, XCircle, PaperPlaneTilt, EnvelopeSimple, ClipboardText,
} from "@phosphor-icons/react";

const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700",
  sent: "bg-blue-50 text-blue-700",
  accepted: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  expired: "bg-amber-50 text-amber-700",
  converted: "bg-purple-50 text-purple-700",
};

export default function Quotations() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [emailFor, setEmailFor] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [{ data: r }, { data: s }] = await Promise.all([
        api.get("/quotations", { params: { q, status: statusFilter } }),
        api.get("/quotations-stats"),
      ]);
      setRows(r || []);
      setStats(s || { total: 0, by_status: {} });
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/quotations/${id}/status`, { status });
      toast.success(`Status → ${status}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const convertToBoq = async (id) => {
    if (!window.confirm("Convert this accepted quotation into a new BOQ project? A BOQ will be created with these line items.")) return;
    try {
      const { data } = await api.post(`/quotations/${id}/convert-to-boq`);
      toast.success(`Created ${data.boq_no}. Opening BOQ…`);
      await load();
      navigate("/ra-billing");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const convertToSo = async (id) => {
    try {
      const { data } = await api.post(`/quotations/${id}/convert-to-so`);
      toast.success(`Created Sales Order ${data.so_no}. Opening Sales Orders…`);
      await load();
      navigate("/sales-orders");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const remove = async (id, no) => {
    if (!window.confirm(`Delete quotation ${no}? This cannot be undone.`)) return;
    try {
      await api.delete(`/quotations/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const totalPipeline = useMemo(() =>
    Object.values(stats.by_status || {}).reduce((s, x) => s + (x.amount || 0), 0),
    [stats]);

  return (
    <div className="space-y-4 p-4" data-testid="quotations-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold font-display text-slate-900 tracking-tight">Quotations</h1>
          <div className="text-[11.5px] text-slate-500">{stats.total} total · pipeline value {fmtMoney(totalPipeline)}</div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="h-8" onClick={load} data-testid="quotes-refresh">
            <ArrowsClockwise size={12} className="mr-1" /> Refresh
          </Button>
          <Button onClick={() => setCreating(true)} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white" data-testid="new-quote-btn">
            <Plus size={13} className="mr-1" /> New Quotation
          </Button>
        </div>
      </div>

      {/* Status pipeline */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="quote-stats">
        {["draft", "sent", "accepted", "rejected", "expired", "converted"].map((s) => {
          const st = stats.by_status?.[s] || { count: 0, amount: 0 };
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
              className={`text-left border rounded-sm px-3 py-2 hover:border-[#EA580C]/40 transition-colors ${statusFilter === s ? "border-[#EA580C] bg-orange-50" : "border-slate-200 bg-white"}`}
              data-testid={`stat-${s}`}
            >
              <div className="text-[9.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">{s}</div>
              <div className="text-[15px] font-bold text-slate-900 mt-0.5">{st.count}</div>
              <div className="text-[10.5px] text-slate-500 font-mono-num">{fmtMoney(st.amount)}</div>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-3 py-2">
        <MagnifyingGlass size={14} className="text-slate-400" />
        <Input placeholder="Search quote #, client, subject…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-[12px] w-96" data-testid="quotes-search" />
        {(q || statusFilter) && (
          <Button variant="ghost" size="sm" className="h-8 text-[11.5px] text-red-600" onClick={() => { setQ(""); setStatusFilter(""); }}>
            Clear filters
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="text-[11.5px]">
              <TableHeader>
                <TableRow className="bg-[#0F172A]">
                  <TableHead className="text-white/90 w-32">Quote #</TableHead>
                  <TableHead className="text-white/90 w-24">Date</TableHead>
                  <TableHead className="text-white/90 w-52">Client</TableHead>
                  <TableHead className="text-white/90">Subject</TableHead>
                  <TableHead className="text-white/90 text-right w-32">Grand Total</TableHead>
                  <TableHead className="text-white/90 text-center w-24">Status</TableHead>
                  <TableHead className="text-white/90 w-72">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">
                    {busy ? "Loading…" : "No quotations yet. Create your first one!"}
                  </TableCell></TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`quote-row-${r.quote_no}`}>
                    <TableCell className="font-mono font-semibold text-slate-900">{r.quote_no}</TableCell>
                    <TableCell className="text-[11px]">{fmtDate(r.date)}</TableCell>
                    <TableCell className="text-[11.5px] text-slate-800">{r.client_name || "—"}</TableCell>
                    <TableCell className="text-[11px] text-slate-600">{r.subject || "—"}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold">{fmtMoney(r.grand_total)}</TableCell>
                    <TableCell className="text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLORS[r.status] || "bg-slate-100"}`}>
                        {r.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" onClick={() => setEditing(r.id)} data-testid={`quote-open-${r.id}`}>
                          <PencilLine size={11} className="mr-0.5" /> Open
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="Download PDF" onClick={() => downloadWithToast(`/quotations/${r.id}/pdf`, `${r.quote_no}.pdf`).catch(() => {})} data-testid={`quote-pdf-${r.id}`}>
                          <FilePdf size={11} />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="Download Excel" onClick={() => downloadWithToast(`/quotations/${r.id}/excel`, `${r.quote_no}.xlsx`).catch(() => {})} data-testid={`quote-excel-${r.id}`}>
                          <FileXls size={11} />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px]" title="Send by Email" onClick={() => setEmailFor(r)} data-testid={`quote-email-${r.id}`}>
                          <EnvelopeSimple size={11} />
                        </Button>
                        {r.status === "draft" && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-blue-600 border-blue-200 hover:bg-blue-50" title="Mark sent" onClick={() => setStatus(r.id, "sent")}>
                            <PaperPlaneTilt size={11} />
                          </Button>
                        )}
                        {r.status === "sent" && (
                          <>
                            <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-emerald-700 border-emerald-200 hover:bg-emerald-50" title="Accept" onClick={() => setStatus(r.id, "accepted")}>
                              <CheckCircle size={11} />
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-red-600 border-red-200 hover:bg-red-50" title="Reject" onClick={() => setStatus(r.id, "rejected")}>
                              <XCircle size={11} />
                            </Button>
                          </>
                        )}
                        {r.status === "accepted" && !r.converted_boq_id && (
                          <>
                            <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-blue-700 border-blue-200 hover:bg-blue-50" title="Convert to Sales Order" onClick={() => convertToSo(r.id)} data-testid={`quote-to-so-${r.id}`}>
                              <ClipboardText size={11} className="mr-0.5" /> SO
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-purple-700 border-purple-200 hover:bg-purple-50" title="Convert directly to BOQ" onClick={() => convertToBoq(r.id)} data-testid={`quote-convert-${r.id}`}>
                              <ArrowRight size={11} className="mr-0.5" /> BOQ
                            </Button>
                          </>
                        )}
                        {r.status !== "converted" && (
                          <Button size="sm" variant="outline" className="h-7 rounded-sm text-[11px] text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(r.id, r.quote_no)} data-testid={`quote-delete-${r.id}`}>
                            <Trash size={11} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {creating && <QuoteEditorSheet onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <QuoteEditorSheet quoteId={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {emailFor && (
        <SendEmailDialog
          endpoint={`/quotations/${emailFor.id}/send-email`}
          docNo={emailFor.quote_no}
          docKind="Quotation"
          defaultTo={emailFor.email || ""}
          onClose={() => setEmailFor(null)}
          onSent={() => { setEmailFor(null); load(); }}
        />
      )}
    </div>
  );
}

